import { makeFormatValue } from '@/lib/units';
import { makeFiscalPeriodLabeler } from '@/lib/fiscalLabels';

// Wrap a base formatValue so measures with a known unit code (e.g. a CERTIFIED
// ratio → percent_decimal) render with that unit; everything else falls back
// to the base formatter. `measureUnits` is the `_measure_units` map the backend
// attaches to query results.
export function withMeasureUnits(baseFormatValue, measureUnits, overriddenFields) {
  if (!measureUnits || !Object.keys(measureUnits).length) return baseFormatValue;
  const unitFmt = makeFormatValue(measureUnits, baseFormatValue);
  // A user's explicit Properties format (or a per-field override) must WIN over
  // the server's auto-detected `_measure_units` (which auto-scales $ to K/M).
  if (!overriddenFields || !overriddenFields.size) return unitFmt;
  return (val, fieldName) => (overriddenFields.has(fieldName)
    ? baseFormatValue(val, fieldName)
    : unitFmt(val, fieldName));
}


// Safe math expression evaluator — uses tokenizer + recursive descent parser (no eval/Function)
export function safeEvalMath(expr) {
  try {
    const tokens = expr.match(/(\d+\.?\d*(?:e[+-]?\d+)?|[+\-*/().])/gi);
    if (!tokens) return 0;
    let pos = 0;
    const peek = () => tokens[pos];
    const consume = () => tokens[pos++];

    function parseExpr() {
      let left = parseTerm();
      while (peek() === '+' || peek() === '-') {
        const op = consume();
        const right = parseTerm();
        left = op === '+' ? left + right : left - right;
      }
      return left;
    }
    function parseTerm() {
      let left = parseFactor();
      while (peek() === '*' || peek() === '/') {
        const op = consume();
        const right = parseFactor();
        left = op === '*' ? left * right : (right !== 0 ? left / right : 0);
      }
      return left;
    }
    function parseFactor() {
      if (peek() === '(') { consume(); const val = parseExpr(); if (peek() === ')') consume(); return val; }
      if (peek() === '-') { consume(); return -parseFactor(); }
      const n = parseFloat(consume());
      return isNaN(n) ? 0 : n;
    }

    const result = parseExpr();
    return typeof result === 'number' && isFinite(result) ? Math.round(result * 100) / 100 : 0;
  } catch { return 0; }
}

// Extract Y/M/D/H/M from an ISO-ish date or datetime string WITHOUT constructing
// a Date (avoids timezone drift and tolerates 6-digit microseconds like
// '2013-07-05T00:00:00.010000'). Returns null when the value isn't ISO-shaped.
function parseISODateParts(s) {
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3], hh: m[4] ? +m[4] : 0, mm: m[5] ? +m[5] : 0 };
}
const _MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const _DATE_FMTS = ['MM/DD/YYYY', 'YYYY-MM-DD', 'DD MMM YYYY', 'MMM YYYY', 'YYYY', 'MMM D, YYYY'];

// Format a value based on field configuration and overrides.
// `fiscalStartMonth` (1-12) relabels period dimensions as fiscal periods
// ("FY2024 Q1") when the model's fiscal year does not start in January.
export function createFormatValue(allFields, fieldOverrides, fiscalStartMonth = 1) {
  const fiscalLabeler = makeFiscalPeriodLabeler(fiscalStartMonth);
  return (value, fieldName) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    if (fiscalLabeler) {
      const df = allFields.find(ff => ff.name === fieldName);
      if (df && df.semantic_type === 'dimension') {
        const fl = fiscalLabeler(value);
        if (fl) return fl;
      }
    }
    const ovr = fieldOverrides[fieldName];
    const fmt = ovr && ovr.format ? ovr.format : null;
    const fieldMeta = allFields.find(f => f.name === fieldName);
    const effectiveFmt = fmt || (!fmt && fieldMeta && fieldMeta.format) || '';

    // String transforms + date-string reformatting.
    if (typeof value === 'string') {
      if (effectiveFmt === 'UPPER' || effectiveFmt === 'uppercase') return value.toUpperCase();
      if (effectiveFmt === 'lower' || effectiveFmt === 'lowercase') return value.toLowerCase();
      if (effectiveFmt === 'Title' || effectiveFmt === 'titlecase') return value.replace(/\w\S*/g, w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
      const parts = parseISODateParts(value);
      if (parts) {
        const mon = _MON[parts.mo - 1] || '';
        const pad = n => String(n).padStart(2, '0');
        // Apply the configured date format (case-insensitive, so 'mm/dd/yyyy'
        // works as well as 'MM/DD/YYYY').
        const fmtU = effectiveFmt.toUpperCase();
        if (_DATE_FMTS.includes(fmtU)) {
          if (fmtU === 'YYYY') return String(parts.y);
          if (fmtU === 'MMM YYYY') return `${mon} ${parts.y}`;
          if (fmtU === 'MMM D, YYYY') return `${mon} ${parts.d}, ${parts.y}`;
          if (fmtU === 'MM/DD/YYYY') return `${pad(parts.mo)}/${pad(parts.d)}/${parts.y}`;
          if (fmtU === 'YYYY-MM-DD') return `${parts.y}-${pad(parts.mo)}-${pad(parts.d)}`;
          if (fmtU === 'DD MMM YYYY') return `${pad(parts.d)} ${mon} ${parts.y}`;
        }
        // Any date value that still carries a time component — or any date-typed
        // field — must never render the raw '2018-11-23T06:15:00'. Drop the time
        // and show a clean date (applies even when an unrecognised format was set).
        const looksDatetime = /[T ]\d{2}:\d{2}/.test(value);
        const isDateField = fieldMeta && (/(date|time)/i.test(fieldMeta.data_type || '') || fieldMeta.data_category === 'Date');
        if (looksDatetime || isDateField) {
          return `${parts.y}-${pad(parts.mo)}-${pad(parts.d)}`;
        }
      }
      return String(value);
    }

    if (!effectiveFmt || typeof value !== 'number') {
      if (typeof value === 'number') {
        if (fieldMeta && fieldMeta.semantic_type === 'dimension') return String(value);
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
      }
      return String(value);
    }
    // Auto-scaling helpers (K/M/B) — pick the tier from the magnitude.
    const _autoScale = (v, prefix) => {
      const a = Math.abs(v);
      if (a >= 1e9) return prefix + (v / 1e9).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'B';
      if (a >= 1e6) return prefix + (v / 1e6).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'M';
      if (a >= 1e3) return prefix + (v / 1e3).toLocaleString(undefined, { maximumFractionDigits: 1 }) + 'K';
      return prefix + v.toLocaleString(undefined, { maximumFractionDigits: prefix ? 0 : 2 });
    };
    // Fixed-scale (M/K/B): when scaling collapses a non-zero value to 0.0 at
    // one decimal (e.g. $594 under '$#,##0.0M' → "$0.0M"), fall back to the
    // auto-scaled real amount so users see "$594" instead of a misleading "$0M".
    const _fixedScale = (v, divisor, suffix, prefix) => {
      const scaled = v / divisor;
      if (v !== 0 && Math.abs(scaled) < 0.05) return _autoScale(v, prefix);
      return prefix + scaled.toLocaleString(undefined, { maximumFractionDigits: 1 }) + suffix;
    };
    if (effectiveFmt === '0') return String(Math.round(value));
    if (effectiveFmt === '#,##0') return Math.round(value).toLocaleString();
    if (effectiveFmt === '#,##0.00') return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (effectiveFmt === '#,##0.0') return value.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    if (effectiveFmt === '$#,##0') return '$' + Math.round(value).toLocaleString();
    if (effectiveFmt === '$#,##0.00') return '$' + value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (effectiveFmt === '$auto') return _autoScale(value, '$');
    if (effectiveFmt === '#auto') return _autoScale(value, '');
    if (effectiveFmt === '$#,##0.0M') return _fixedScale(value, 1e6, 'M', '$');
    if (effectiveFmt === '$#,##0.0B') return _fixedScale(value, 1e9, 'B', '$');
    if (effectiveFmt === '$#,##0.0K') return _fixedScale(value, 1e3, 'K', '$');
    if (effectiveFmt === '#,##0.0K') return _fixedScale(value, 1e3, 'K', '');
    if (effectiveFmt === '#,##0.0M') return _fixedScale(value, 1e6, 'M', '');
    if (effectiveFmt === '#,##0.0B') return _fixedScale(value, 1e9, 'B', '');
    if (effectiveFmt === '#0.0%') return value.toFixed(1) + '%';
    if (effectiveFmt === '#0.00%') return value.toFixed(2) + '%';
    if (effectiveFmt === '#0%') return Math.round(value) + '%';
    if (effectiveFmt === '+#0.0%;-#0.0%') return (value >= 0 ? '+' : '') + value.toFixed(1) + '%';
    if (effectiveFmt === '#,##0.0x') return value.toFixed(1) + 'x';
    return typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : String(value);
  };
}
